import { CardSize, CollectionSchemaType, CollectionCardImageDir } from '@next-space/fe-api-idl';
import { keyBy } from 'lodash-es';
import { ColorKey } from 'src/colors';
import type { IconName } from 'src/common/components/icon';
import type { CollectionProperty } from 'src/hooks/collection-view/use-properties';
import { appI18n } from '@flowus/common/i18n';

export const CardSizeWidthMap = {
  [CardSize.SMALL]: 192,
  [CardSize.MEDIUM]: 272,
  [CardSize.LARGE]: 332,
};

export const getImageRadio = (imageDir: CollectionCardImageDir) => {
  return imageDir === CollectionCardImageDir.VERTICAL ? 1.5 : 0.55;
};

export const getCardSize = (cardSize: CardSize, imageDir: CollectionCardImageDir) => {
  const size = CardSizeWidthMap[cardSize];
  return imageDir === CollectionCardImageDir.VERTICAL ? size - 10 : size;
};

export const CardContentWidthMap = {
  [CardSize.SMALL]: 100,
  [CardSize.MEDIUM]: 150,
  [CardSize.LARGE]: 180,
};

export const getPropertyWidth = (property: CollectionProperty | undefined) => {
  const defaultWidth =
    property?.type === CollectionSchemaType.ID_NUMBER ? ID_NUMBER_DEFAULT_WIDTH : DEFAULT_COL_WIDTH;
  const width = !property?.width ? defaultWidth : Math.max(MIN_COL_WIDTH, property.width);
  return width;
};

export const DEFAULT_COL_WIDTH = 180;
export const ID_NUMBER_DEFAULT_WIDTH = 66;
export const MIN_COL_WIDTH = 38;
export const MAX_COL_WIDTH = 1200;
export const DEFAULT_ROW_HEIGHT = 37;
export interface MetaColor {
  key: ColorKey;
  name: string;
}

export const COLORS = [
  { key: ColorKey.grey, name: appI18n.t('common.grey') },
  { key: ColorKey.brown, name: appI18n.t('common.brown') },
  { key: ColorKey.orange, name: appI18n.t('bitable.view.color.orange') },
  { key: ColorKey.yellow, name: appI18n.t('common.yellow') },
  { key: ColorKey.green, name: appI18n.t('common.green') },
  { key: ColorKey.blue, name: appI18n.t('common.blue') },
  { key: ColorKey.purple, name: appI18n.t('common.purple') },
  { key: ColorKey.pink, name: appI18n.t('common.pink') },
  { key: ColorKey.red, name: appI18n.t('common.red') },
  // { key: ColorKey.light_blue, name: '荧光蓝' },
  // { key: ColorKey.light_red, name: '荧光红' },
  // { key: ColorKey.light_yellow, name: '荧光黄' },
  // { key: ColorKey.light_purple, name: '荧光紫' },
  // { key: ColorKey.light_green, name: '荧光绿' },
] as const;

export const ICON_MAP: Record<CollectionSchemaType, IconName> = {
  [CollectionSchemaType.TITLE]: 'IcTitle',
  [CollectionSchemaType.NUMBER]: 'IcChartNumber',
  [CollectionSchemaType.TEXT]: 'IcPropertyText',
  [CollectionSchemaType.CHECKBOX]: 'IcCheck',
  [CollectionSchemaType.SELECT]: 'IcExclusiveChoice',
  [CollectionSchemaType.MULTI_SELECT]: 'IcMultipleChoice',
  [CollectionSchemaType.CREATED_AT]: 'IcClockPlus',
  [CollectionSchemaType.UPDATED_AT]: 'IcClockCheck',
  [CollectionSchemaType.DATE]: 'IcCalendar',
  [CollectionSchemaType.PERSON]: 'IcMember',
  [CollectionSchemaType.FILE]: 'IcPaperclip',
  [CollectionSchemaType.URL]: 'IcLink',
  [CollectionSchemaType.EMAIL]: 'IcMail',
  [CollectionSchemaType.PHONE]: 'IcCall',
  [CollectionSchemaType.CREATED_BY]: 'IcUserAdd',
  [CollectionSchemaType.UPDATED_BY]: 'IcUserEdit',
  [CollectionSchemaType.FORMULA]: 'IcFormula',
  [CollectionSchemaType.RELATION]: 'IcAssociation',
  [CollectionSchemaType.ROLLUP]: 'IcSearch',
  [CollectionSchemaType.ID_NUMBER]: 'IcMenuNum',
} as const;

export const PROPERTIES = [
  {
    type: CollectionSchemaType.TEXT,
    title: appI18n.t('bitable.view.field_type.text'),
    icon: ICON_MAP.text,
  },
  {
    type: CollectionSchemaType.NUMBER,
    title: appI18n.t('common.number'),
    icon: ICON_MAP.number,
  },
  {
    type: CollectionSchemaType.CHECKBOX,
    title: appI18n.t('bitable.view.field_type.checkbox'),
    icon: ICON_MAP.checkbox,
  },
  {
    type: CollectionSchemaType.SELECT,
    title: appI18n.t('common.single_select'),
    icon: ICON_MAP.select,
  },
  {
    type: CollectionSchemaType.MULTI_SELECT,
    title: appI18n.t('bitable.view.field_type.multi_select'),
    icon: ICON_MAP.multi_select,
  },
  {
    type: CollectionSchemaType.DATE,
    title: appI18n.t('common.date'),
    icon: ICON_MAP.date,
  },
  {
    type: CollectionSchemaType.PERSON,
    title: appI18n.t('common.person'),
    icon: ICON_MAP.person,
  },
  {
    type: CollectionSchemaType.FILE,
    title: appI18n.t('bitable.view.field_type.file'),
    icon: ICON_MAP.file,
  },
  {
    type: CollectionSchemaType.URL,
    title: appI18n.t('bitable.view.field_type.url'),
    icon: ICON_MAP.url,
  },
  {
    type: CollectionSchemaType.EMAIL,
    title: appI18n.t('common.email'),
    icon: ICON_MAP.email,
  },
  {
    type: CollectionSchemaType.PHONE,
    title: appI18n.t('bitable.view.field_type.phone'),
    icon: ICON_MAP.phone,
  },
  {
    type: CollectionSchemaType.FORMULA,
    title: appI18n.t('common.formula'),
    icon: ICON_MAP.formula,
  },
  {
    type: CollectionSchemaType.RELATION,
    title: appI18n.t('common.relation'),
    icon: ICON_MAP.relation,
  },
  {
    type: CollectionSchemaType.ROLLUP,
    title: appI18n.t('bitable.view.field_type.rollup'),
    icon: ICON_MAP.rollup,
  },
  {
    type: CollectionSchemaType.CREATED_AT,
    title: appI18n.t('common.created_at'),
    icon: ICON_MAP.created_at,
  },
  {
    type: CollectionSchemaType.CREATED_BY,
    title: appI18n.t('bitable.view.field_type.created_by'),
    icon: ICON_MAP.created_by,
  },
  {
    type: CollectionSchemaType.UPDATED_AT,
    title: appI18n.t('bitable.view.field_type.updated_at'),
    icon: ICON_MAP.updated_at,
  },
  {
    type: CollectionSchemaType.UPDATED_BY,
    title: appI18n.t('bitable.view.field_type.updated_by'),
    icon: ICON_MAP.updated_by,
  },
];

export const PROPERTIES_FORM = [
  {
    type: CollectionSchemaType.TEXT,
    title: appI18n.t('bitable.view.field_type.text'),
    icon: ICON_MAP.text,
  },
  {
    type: CollectionSchemaType.NUMBER,
    title: appI18n.t('common.number'),
    icon: ICON_MAP.number,
  },
  {
    type: CollectionSchemaType.CHECKBOX,
    title: appI18n.t('bitable.view.field_type.checkbox'),
    icon: ICON_MAP.checkbox,
  },
  {
    type: CollectionSchemaType.SELECT,
    title: appI18n.t('common.single_select'),
    icon: ICON_MAP.select,
  },
  {
    type: CollectionSchemaType.MULTI_SELECT,
    title: appI18n.t('bitable.view.field_type.multi_select'),
    icon: ICON_MAP.multi_select,
  },
  {
    type: CollectionSchemaType.DATE,
    title: appI18n.t('common.date'),
    icon: ICON_MAP.date,
  },
  {
    type: CollectionSchemaType.PERSON,
    title: appI18n.t('common.person'),
    icon: ICON_MAP.person,
  },
  {
    type: CollectionSchemaType.FILE,
    title: appI18n.t('bitable.view.field_type.file'),
    icon: ICON_MAP.file,
  },
  {
    type: CollectionSchemaType.URL,
    title: appI18n.t('bitable.view.field_type.url'),
    icon: ICON_MAP.url,
  },
  {
    type: CollectionSchemaType.EMAIL,
    title: appI18n.t('common.email'),
    icon: ICON_MAP.email,
  },
  {
    type: CollectionSchemaType.PHONE,
    title: appI18n.t('bitable.view.field_type.phone'),
    icon: ICON_MAP.phone,
  },
];

/** 收集表需要过滤的属性 */
export const FILTER_PROPERTIES_FORM = [
  CollectionSchemaType.CREATED_AT,
  CollectionSchemaType.CREATED_BY,
  CollectionSchemaType.UPDATED_AT,
  CollectionSchemaType.UPDATED_BY,
  CollectionSchemaType.RELATION,
  CollectionSchemaType.ROLLUP,
  CollectionSchemaType.FORMULA,
];

export const READONLY_PROPERTIES = [
  CollectionSchemaType.CREATED_AT,
  CollectionSchemaType.UPDATED_AT,
  CollectionSchemaType.CREATED_BY,
  CollectionSchemaType.UPDATED_BY,
];

export const COMPUTED_PROPERTIES = [CollectionSchemaType.FORMULA, CollectionSchemaType.ROLLUP];

export const PROPERTIES_MAP = keyBy(PROPERTIES, 'type');
export const PROPERTIES_FORM_MAP = keyBy(PROPERTIES_FORM, 'type');

export const TableClassName = {
  tableFooter: 'table-footer',
  tableHeader: 'table-header',
  tableSortManager: 'table-sort-manager',
};

export enum Direction {
  left = 'left',
  right = 'right',
}
export const DirectionMap = [Direction.left, Direction.right];

export const DEFAULT_BITABLE_PAGE_SIZE = 50;
export const MAX_BITABLE_PAGE_SIZE = 9999;

export const OVERSCAN = 200;

export const isEnableVirtuoso = (count: number) => count >= 300;

export const AUTO_LOAD_COUNT = 10;
